/**
 * sizerResultUi — the RESPONSE-SIDE render kit shared by the three sizers.
 *
 * Every component here renders a block the backend already computes and ships:
 * the guideline-check list, the leverage requested-vs-max table, the DSCR coupon
 * ladder, the server-generated sensitivity matrix, the closing-cost ladder, the
 * borrower-class explanation, the 422 missing-requirements gate, the payload
 * warnings and the disclaimer.
 *
 * The rule these all obey (pricing-scenario-sizers.md, "Shipping requirements"
 * §1): RENDER ONLY. No pricing or guideline math happens in this file — not a
 * DSCR, not a PITIA, not a leverage maximum, not a sensitivity cell, not even a
 * color threshold (the matrix ships its own `bands`). If a derived display is
 * missing, it gets ADDED TO THE RESPONSE, not computed here. The prototype's
 * client-side cell math is exactly the drift this replaces.
 */
import { useState } from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import JsonPreview from "../../components/common/JsonPreview";
import { SZ, SectionLabel, Badge } from "./sizerUi";
import { fmtMoney, fmtPct, fmtPrice, fmtRatio, humanize } from "./sizerFormat";

/* ============================================================ Primitives */

/** Top-bordered group inside a results card. */
export const ResultGroup = ({ label, action, children, sx }) => (
  <Box sx={{ pt: 2, mt: 2, borderTop: `1px solid ${SZ.line}`, ...sx }}>
    <Flx jb ac sx={{ mb: 0.5 }}>
      <SectionLabel>{label}</SectionLabel>
      {action}
    </Flx>
    {children}
  </Box>
);

const TONES = {
  bad: { fg: SZ.bad, bg: SZ.badBg, border: `${SZ.bad}33`, Icon: CancelRoundedIcon },
  warn: { fg: SZ.warn, bg: SZ.warnBg, border: `${SZ.warn}33`, Icon: ErrorOutlineRoundedIcon },
  good: { fg: SZ.good, bg: SZ.goodBg, border: `${SZ.good}33`, Icon: CheckCircleRoundedIcon },
  info: { fg: SZ.teal700, bg: SZ.teal50, border: `${SZ.teal600}33`, Icon: InfoOutlinedIcon },
};

/** Tinted callout box. `tone`: bad | warn | good | info. */
export const Callout = ({ tone = "info", title, children, sx }) => {
  const t = TONES[tone] || TONES.info;
  const Icon = t.Icon;
  return (
    <Box
      sx={{
        border: `1px solid ${t.border}`,
        bgcolor: t.bg,
        borderRadius: SZ.radiusSm,
        p: 1.25,
        ...sx,
      }}
    >
      {title && (
        <Flx ac g={1} sx={{ mb: children ? 0.75 : 0 }}>
          <Icon sx={{ color: t.fg, fontSize: 17 }} />
          <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: t.fg }}>{title}</Txt>
        </Flx>
      )}
      {children && (
        <Box sx={{ fontSize: 12.5, color: SZ.ink700 }}>{children}</Box>
      )}
    </Box>
  );
};

/* ============================================================ Result table */

/**
 * ResultTable — the house table look, ported from the Admin → Pricing screens
 * (ADMIN_UI.md "Tables") so a sizer result reads like the pricing grids the same
 * desk uses: a fully bordered rounded block, small-caps faint headers, hairline
 * row rules, and tabular mono figures.
 *
 * THE RULE THIS EXISTS TO ENFORCE: **a column's header and its cells always
 * align the same way.** Alignment is declared once per column, on the column,
 * and applied to both the `<th>` and every `<td>` — it is not possible to align
 * a header one way and its cells another, which is the drift the admin screens'
 * `num`/`key`/`ctr` classes were introduced to prevent.
 *
 *   num — right, tabular mono. Every figure: rates, prices, DSCR, amounts.
 *   ctr — centre, tabular mono. A figure that reads as a key (a coupon).
 *   key — left, mono. Identifiers: metric names, class letters, tiers.
 *   txt — left, proportional. Prose.
 *
 * Not AG Grid, per CLAUDE.md's carve-out and the note on each caller below:
 * these are fixed 3–6 row readouts of ONE priced scenario, several inside a
 * 360px rail, with nothing to sort, filter, page or persist. The *look* is
 * shared; the machinery would not earn its height here.
 */
const ALIGN = {
  num: { textAlign: "right", fontFamily: SZ.mono, fontVariantNumeric: "tabular-nums" },
  ctr: { textAlign: "center", fontFamily: SZ.mono, fontVariantNumeric: "tabular-nums" },
  key: { textAlign: "left", fontFamily: SZ.mono },
  txt: { textAlign: "left" },
};

export const ResultTable = ({
  columns = [],
  rows = [],
  getRowKey,
  // Per-row style hook — used to tint the winning ladder rung / resolved class.
  rowSx,
  // Per-row DOM props (onMouseEnter/onMouseLeave …) — lets a caller wire a row
  // to something outside the table, e.g. the class ladder in the results rail.
  rowProps,
  minWidth,
  sx,
}) => {
  if (!rows.length) return null;
  return (
    <Box
      sx={{
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radiusSm,
        // The border lives on the wrapper, and the last row drops its own, so
        // the two can never stack into a 2px line.
        overflow: "hidden",
        overflowX: "auto",
        ...sx,
      }}
    >
      <Box
        component="table"
        sx={{ width: "100%", minWidth, borderCollapse: "collapse", tableLayout: "auto" }}
      >
        <Box component="thead">
          <Box component="tr">
            {columns.map((c, i) => (
              <Box
                component="th"
                key={c.label || i}
                sx={{
                  ...ALIGN[c.align || "txt"],
                  // Headers are small-caps and faint whatever the column's cell
                  // font is — only the ALIGNMENT is inherited from the column.
                  fontFamily: "inherit",
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: ".07em",
                  textTransform: "uppercase",
                  color: SZ.ink400,
                  padding: "8px 12px",
                  borderBottom: `1px solid ${SZ.line}`,
                  whiteSpace: "nowrap",
                  width: c.width,
                }}
              >
                {c.label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, ri) => (
            <Box
              component="tr"
              key={getRowKey ? getRowKey(row, ri) : ri}
              {...(rowProps ? rowProps(row, ri) : null)}
              sx={{
                "& > td": { borderBottom: `1px solid ${SZ.line}` },
                "&:last-of-type > td": { borderBottom: "none" },
                ...(rowSx ? rowSx(row, ri) : null),
              }}
            >
              {columns.map((c, ci) => (
                <Box
                  component="td"
                  key={ci}
                  sx={{
                    ...ALIGN[c.align || "txt"],
                    fontSize: 12.5,
                    color: SZ.ink900,
                    padding: "8px 12px",
                    verticalAlign: "middle",
                  }}
                >
                  {c.render(row, ri)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/* ============================================================ Guideline check */

// severity -> dot, per shipping requirement 3: hard = red, soft =
// approval-required (amber, labelled with its approver), review = amber.
const SEVERITY = {
  hard: { color: SZ.bad, label: "Hard" },
  soft: { color: SZ.warn, label: "Approval" },
  review: { color: SZ.warn, label: "Review" },
};

const Dot = ({ color }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
      mt: "5px",
    }}
  />
);

/**
 * The Guideline Check panel: `guidelineExceptions[]` by severity plus the
 * facility gate's own failed checks. `leverageReduction` is printed where the
 * exception carries one — it is the reduction already applied to the grid max
 * server-side, not something to subtract again.
 */
/* Optional `onRequestException(finding)`: a hard or soft row gets a "Request
   exception" button (pricing-scenario-sizers.md §5.2 / §6 — pricing exceptions
   start at the sizer). A row whose code is in `pendingCodes` (filed, still open)
   shows "Requested · pending" instead, so the same exception is never filed
   twice. Info rows never get a button. */
export const GuidelineCheckList = ({
  exceptions = [],
  facilityHardExceptions,
  onRequestException,
  pendingCodes = [],
}) => {
  const failed = facilityHardExceptions?.failed_checks || [];
  const notApplicable = facilityHardExceptions?.applicable === false;

  if (!exceptions.length && !failed.length) {
    return (
      <Flx g={0.75} sx={{ alignItems: "flex-start", py: 0.4 }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 15, color: SZ.good, mt: "1px" }} />
        <Txt component="span" sx={{ fontSize: 12.5, color: SZ.ink700 }}>
          No guideline exceptions raised
          {notApplicable ? " (no facility gate for this product)" : ""}.
        </Txt>
      </Flx>
    );
  }

  return (
    <>
      {exceptions.map((e, i) => {
        const s = SEVERITY[e.severity] || SEVERITY.review;
        return (
          <Flx key={`${e.code}-${i}`} g={0.85} sx={{ alignItems: "flex-start", py: 0.5 }}>
            <Dot color={s.color} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Txt component="div" sx={{ fontSize: 12.5, color: SZ.ink700 }}>
                {e.message}
              </Txt>
              <Flx g={1} sx={{ mt: "2px", flexWrap: "wrap" }}>
                <Txt component="span" sx={{ fontSize: 11, fontWeight: 700, color: s.color }}>
                  {s.label}
                </Txt>
                {e.approver && (
                  <Txt component="span" sx={{ fontSize: 11, color: SZ.ink400 }}>
                    approver: {humanize(e.approver)}
                  </Txt>
                )}
                {e.leverageReduction != null && (
                  <Txt component="span" sx={{ fontSize: 11, color: SZ.warn }}>
                    −{fmtPct(e.leverageReduction, 2)} leverage
                  </Txt>
                )}
              </Flx>
              {onRequestException &&
                (e.severity === "hard" || e.severity === "soft") &&
                (pendingCodes.includes(e.code) ? (
                  <Txt
                    component="div"
                    sx={{
                      display: "inline-block",
                      mt: "6px",
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: ".04em",
                      textTransform: "uppercase",
                      p: "2px 8px",
                      borderRadius: "99px",
                      background: SZ.warnBg,
                      color: SZ.warn,
                    }}
                  >
                    Requested · pending
                  </Txt>
                ) : (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onRequestException(e)}
                    sx={{
                      mt: "6px",
                      fontFamily: "inherit",
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: SZ.teal700,
                      background: SZ.card,
                      border: `1px solid ${SZ.teal100}`,
                      borderRadius: "6px",
                      p: "3px 9px",
                      cursor: "pointer",
                      "&:hover": { background: SZ.teal50 },
                    }}
                  >
                    Request exception
                  </Box>
                ))}
            </Box>
          </Flx>
        );
      })}

      {failed.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Txt sx={{ fontSize: 11, fontWeight: 700, color: SZ.bad, mb: 0.5 }}>
            Facility hard exceptions
          </Txt>
          {failed.map((f, i) => (
            <Flx key={i} g={0.85} sx={{ alignItems: "flex-start", py: 0.35 }}>
              <Dot color={SZ.bad} />
              <Txt component="span" sx={{ fontSize: 12.5, color: SZ.ink700, flex: 1 }}>
                {typeof f === "string" ? f : f.message || f.reason || JSON.stringify(f)}
              </Txt>
            </Flx>
          ))}
        </Box>
      )}
    </>
  );
};

/* ============================================================ Leverage */

/**
 * Requested vs MAXIMUM per metric (LTC / LTV / LTARV / TLTC). `maximum` is the
 * grid max AFTER market-data reductions — the response field is `maximum`, and
 * printing it next to requested is the whole point of the table.
 */
export const LeverageTable = ({ leverage = [] }) => (
  <ResultTable
    getRowKey={(m) => m.metric}
    rows={leverage}
    columns={[
      {
        label: "Metric",
        align: "key",
        render: (m) => (
          <Flx ac g={0.75}>
            <Box component="span">{m.metric}</Box>
            {m.over && <Badge tone="fail">Over</Badge>}
          </Flx>
        ),
      },
      {
        label: "Requested",
        align: "num",
        render: (m) => (
          <Box
            component="span"
            sx={{ color: m.over ? SZ.bad : SZ.ink900, fontWeight: m.over ? 700 : 500 }}
          >
            {fmtPct(m.requested, 2)}
          </Box>
        ),
      },
      {
        label: "Max",
        align: "num",
        render: (m) => (
          <Box component="span" sx={{ color: SZ.ink500 }}>
            {fmtPct(m.maximum, 2)}
          </Box>
        ),
      },
    ]}
  />
);

/**
 * The `/scenario/max_leverage` grid: one row PER CLASS, resolved class
 * highlighted. No rate exists on that response by design.
 */
export const LEVERAGE_COLUMNS = [
  ["As-is LTV", "maxAsIsLtv"],
  ["LTC", "maxLtc"],
  ["TLTC", "maxTltc"],
  ["LTARV", "maxLtarv"],
];

// A metric absent on EVERY row isn't a gap in this scenario's data — the product
// simply has no such cap (TLTC on a Fix & Flip purchase). Says so on hover
// rather than leaving a bare dash to be read as "missing".
const NA_REASON =
  "This product's leverage grid carries no cap for this metric — it is not a missing value.";

/**
 * The product's per-class max-leverage grid, `result.maxLeverage`. Row shape:
 * `{borrowerClass, maxAsIsLtv, maxLtc, maxTltc, maxLtarv, isResolvedClass}`.
 *
 * `floors` (from `parseClassFloors`) adds each class's score requirement beside
 * its name, so the table and the ladder in the results rail read as one thing.
 * `hoverClass`/`onHoverClass` are the cross-highlight wiring between them.
 *
 * `showDelta` annotates every other class with its distance from the resolved
 * one. That IS arithmetic, but only the difference between two numbers this same
 * response shipped — no leverage maximum is derived here.
 */
export const MaxLeverageGrid = ({
  rows = [],
  floors = [],
  showDelta = false,
  hoverClass = null,
  onHoverClass,
}) => {
  const floorFor = (cls) =>
    floors.find((f) => f.borrowerClass === cls)?.floor ?? null;
  const resolvedRow = rows.find((r) => r.isResolvedClass) || null;

  return (
    <ResultTable
      minWidth={420}
      rows={rows}
      getRowKey={(r, i) => `${r.borrowerClass}-${i}`}
      rowProps={(r) =>
        onHoverClass
          ? {
              onMouseEnter: () => onHoverClass(r.borrowerClass),
              onMouseLeave: () => onHoverClass(null),
            }
          : null
      }
      rowSx={(r) => ({
        ...(r.isResolvedClass
          ? {
              background: SZ.teal50,
              "& > td:first-of-type": {
                boxShadow: `inset 3px 0 0 ${SZ.teal600}`,
              },
            }
          : hoverClass === r.borrowerClass
            ? { background: SZ.teal50 }
            : null),
        transition: "background .12s",
      })}
      columns={[
        {
          label: "Class",
          align: "key",
          render: (r) => {
            const floor = floorFor(r.borrowerClass);
            return (
              <Flx ac g={0.75} sx={{ flexWrap: "wrap" }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: r.isResolvedClass ? SZ.teal700 : SZ.ink900,
                  }}
                >
                  {r.borrowerClass}
                </Box>
                {r.isResolvedClass && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: ".04em",
                      color: SZ.teal700,
                      background: SZ.card,
                      border: `1px solid ${SZ.teal600}`,
                      borderRadius: "99px",
                      padding: "1px 8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    YOUR CLASS
                  </Box>
                )}
                {floor != null && (
                  <Box
                    component="span"
                    sx={{
                      fontFamily: SZ.mono,
                      fontSize: 10.5,
                      fontWeight: 400,
                      color: SZ.ink400,
                    }}
                  >
                    score ≥ {floor}
                  </Box>
                )}
              </Flx>
            );
          },
        },
        ...LEVERAGE_COLUMNS.map(([label, field]) => ({
          label,
          align: "num",
          render: (r) => {
            if (r[field] == null) {
              return (
                <Tooltip title={NA_REASON} placement="top">
                  <Box component="span" sx={{ color: SZ.ink400, cursor: "default" }}>
                    —
                  </Box>
                </Tooltip>
              );
            }
            // Points of leverage between this class and the resolved one.
            const base = resolvedRow?.[field];
            const diff =
              showDelta && base != null && !r.isResolvedClass
                ? (r[field] - base) * 100
                : null;
            return (
              <Flx column g={0} sx={{ alignItems: "flex-end" }}>
                <Box
                  component="span"
                  sx={{ color: r.isResolvedClass ? SZ.ink900 : SZ.ink500 }}
                >
                  {fmtPct(r[field], 2)}
                </Box>
                {showDelta && r.isResolvedClass && (
                  <Box
                    component="span"
                    sx={{ fontFamily: SZ.mono, fontSize: 10, color: SZ.ink400 }}
                  >
                    your max
                  </Box>
                )}
                {diff != null && (
                  <Box
                    component="span"
                    sx={{
                      fontFamily: SZ.mono,
                      fontSize: 10,
                      color: diff < 0 ? SZ.bad : SZ.good,
                    }}
                  >
                    {diff > 0 ? "+" : ""}
                    {diff.toFixed(2)} pts
                  </Box>
                )}
              </Flx>
            );
          },
        })),
      ]}
    />
  );
};

/* ================================================ Borrower classification */

/**
 * The answer, whole: the resolved class and the leverage it unlocks, together.
 * The class alone is jargon — what a user acts on is the three maxima beside it,
 * so they share one block rather than sitting a scroll apart.
 *
 * Tiles come from the resolved row of `result.maxLeverage`; a metric the product
 * doesn't cap is skipped rather than shown as a dash.
 */
export const ClassAnswer = ({ borrowerClass, totalScore, context, resolvedRow }) => {
  const tiles = resolvedRow
    ? LEVERAGE_COLUMNS.filter(([, f]) => resolvedRow[f] != null).map(
        ([label, f]) => ({ label, value: fmtPct(resolvedRow[f], 1) }),
      )
    : [];

  return (
    <Box>
      <Flx column ac g={0.5} sx={{ textAlign: "center" }}>
        <SectionLabel>Borrower class</SectionLabel>
        <Txt
          component="div"
          sx={{
            fontSize: 46,
            fontWeight: 800,
            letterSpacing: "-.03em",
            lineHeight: 1,
            color: SZ.teal700,
          }}
        >
          {borrowerClass || "—"}
        </Txt>
        <Txt component="div" sx={{ fontSize: 12, color: SZ.ink500 }}>
          {totalScore != null && (
            <>
              Total score{" "}
              <Box
                component="span"
                sx={{ fontFamily: SZ.mono, fontWeight: 600, color: SZ.ink900 }}
              >
                {totalScore}
              </Box>
            </>
          )}
          {context ? `${totalScore != null ? " · " : ""}${context}` : null}
        </Txt>
      </Flx>

      {tiles.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(tiles.length, 3)}, 1fr)`,
            gap: 1.25,
            mt: 2,
          }}
        >
          {tiles.map((t) => (
            <Box
              key={t.label}
              sx={{
                background: "#f4fbfa",
                border: `1px solid ${SZ.teal100}`,
                borderRadius: SZ.radiusSm,
                p: "10px 12px",
              }}
            >
              <Txt
                component="div"
                sx={{ fontFamily: SZ.mono, fontSize: 16, fontWeight: 600, color: SZ.ink900 }}
              >
                {t.value}
              </Txt>
              <Txt
                component="div"
                sx={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: ".05em",
                  textTransform: "uppercase",
                  color: SZ.ink400,
                  mt: "2px",
                }}
              >
                {t.label}
              </Txt>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Score against the class floors — where the sponsor landed and how much room
 * they have before the next class down.
 *
 * The floors come from the response (see `parseClassFloors`); the track spans
 * 0 → the highest floor or the score, whichever is greater, because the response
 * never states a maximum attainable score. Nothing is labelled "of N" for that
 * reason. With no floors parsed, the ladder is skipped and only the equation
 * renders — the numbers stay honest rather than being filled in.
 */
export const ClassFloorLadder = ({
  floors = [],
  totalScore,
  creditScore,
  experienceScore,
  borrowerClass,
  product,
  rows = [],
  hoverClass = null,
  onHoverClass,
}) => {
  // GUC classifies on COMPLETED-PROJECT COUNT, not a points total: for it
  // `ClassificationConfig.classify` returns creditScore = the raw resolved FICO
  // (the 700+ gate on class B) and totalScore = the experience count itself. The
  // RTL "credit pts + experience pts = total" equation is nonsense there — it
  // would render "720 + 5 = 5" — so the two products get different readouts.
  const isGuc = /ground up|guc/i.test(String(product || ""));
  const score = Number(totalScore);
  const hasScore = Number.isFinite(score);
  // 15% headroom so a score sitting at the top of the range still fits its pin.
  const span = Math.max(...floors.map((f) => f.floor), hasScore ? score : 0, 1);
  const pos = (n) => `${Math.min(100, (n / (span * 1.15)) * 100)}%`;

  const resolvedFloor = floors.find(
    (f) => f.borrowerClass === borrowerClass,
  )?.floor;
  const nextDown = floors.find(
    (f) => resolvedFloor != null && f.floor < resolvedFloor,
  );
  const headroom =
    hasScore && resolvedFloor != null ? score - resolvedFloor : null;

  // Leverage lost if the class drops one step — again, the gap between two rows
  // of this response, not a recomputed maximum.
  const here = rows.find((r) => r.isResolvedClass);
  const below = nextDown
    ? rows.find((r) => r.borrowerClass === nextDown.borrowerClass)
    : null;
  const dropPts =
    here?.maxAsIsLtv != null && below?.maxAsIsLtv != null
      ? (here.maxAsIsLtv - below.maxAsIsLtv) * 100
      : null;

  return (
    <Box>
      <SectionLabel>
        {isGuc ? "Experience vs class thresholds" : "Score vs class floors"}
      </SectionLabel>

      {floors.length > 0 && hasScore && (
        <Box sx={{ position: "relative", height: 62, mt: 1.5, mx: "2px" }}>
          <Box
            sx={{
              position: "absolute",
              top: 26,
              left: 0,
              right: 0,
              height: 8,
              borderRadius: "4px",
              background: SZ.line,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 26,
              left: 0,
              height: 8,
              width: pos(score),
              borderRadius: "4px",
              background: `linear-gradient(90deg, ${SZ.teal100}, ${SZ.teal600})`,
              transition: "width .6s cubic-bezier(.2,.8,.2,1)",
            }}
          />

          {floors.map((f) => {
            const reached = score >= f.floor;
            const lit = hoverClass === f.borrowerClass;
            return (
              <Box
                key={f.borrowerClass}
                onMouseEnter={() => onHoverClass?.(f.borrowerClass)}
                onMouseLeave={() => onHoverClass?.(null)}
                sx={{
                  position: "absolute",
                  top: 18,
                  left: pos(f.floor),
                  width: "2px",
                  height: 24,
                  borderRadius: "1px",
                  background: reached ? SZ.teal600 : SZ.lineStrong,
                  cursor: "default",
                }}
              >
                <Txt
                  component="div"
                  sx={{
                    position: "absolute",
                    top: -17,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontFamily: SZ.mono,
                    fontSize: 9.5,
                    color: SZ.ink400,
                  }}
                >
                  {f.floor}
                </Txt>
                <Txt
                  component="div"
                  sx={{
                    position: "absolute",
                    top: 27,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: 10.5,
                    fontWeight: lit ? 700 : 600,
                    whiteSpace: "nowrap",
                    color: lit || reached ? SZ.teal700 : SZ.ink400,
                  }}
                >
                  {f.borrowerClass}
                </Txt>
              </Box>
            );
          })}

          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: pos(score),
              transform: "translateX(-50%)",
              transition: "left .6s cubic-bezier(.2,.8,.2,1)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                background: SZ.ink900,
                color: "#fff",
                fontFamily: SZ.mono,
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: "6px",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -4,
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderLeft: "4px solid transparent",
                  borderRight: "4px solid transparent",
                  borderTop: `4px solid ${SZ.ink900}`,
                },
              }}
            >
              {score}
            </Box>
          </Box>
        </Box>
      )}

      {/* The whole scoring rule in one line. RTL adds two point buckets; GUC
          counts completed projects and uses FICO only as the class-B gate, so
          it gets a side-by-side pair rather than a sum. */}
      <Flx ac g={1} sx={{ mt: floors.length && hasScore ? 2 : 1.5 }}>
        {isGuc ? (
          <>
            <EqPart value={experienceScore} label="Completed projects" total />
            <EqPart value={creditScore} label="Resolved FICO" />
          </>
        ) : (
          <>
            <EqPart value={creditScore} label="Credit pts" />
            <Txt sx={{ fontSize: 12, fontWeight: 600, color: SZ.ink400 }}>+</Txt>
            <EqPart value={experienceScore} label="Experience pts" />
            <Txt sx={{ fontSize: 12, fontWeight: 600, color: SZ.ink400 }}>=</Txt>
            <EqPart value={totalScore} label="Total" total />
          </>
        )}
      </Flx>

      {headroom != null && (
        <Callout
          tone={headroom === 0 ? "warn" : "info"}
          sx={{ mt: 1.75 }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: SZ.ink900 }}>
            {headroom === 0
              ? `At the ${borrowerClass} ${isGuc ? "threshold" : "floor"}.`
              : `${headroom} ${isGuc ? (headroom === 1 ? "project" : "projects") : headroom === 1 ? "pt" : "pts"} above the ${borrowerClass} ${isGuc ? "threshold" : "floor"}.`}
          </Box>{" "}
          {nextDown ? (
            <>
              If {isGuc ? "the completed-project count" : "FICO or experience"}{" "}
              validates lower in underwriting, class drops to{" "}
              {nextDown.borrowerClass}
              {dropPts != null ? (
                <>
                  {" "}
                  — max as-is LTV would fall {dropPts.toFixed(2)} pts to{" "}
                  {fmtPct(below.maxAsIsLtv, 2)}
                </>
              ) : null}
              .
            </>
          ) : (
            <>This is the lowest class — there is no further step down.</>
          )}
        </Callout>
      )}
    </Box>
  );
};

const EqPart = ({ value, label, total }) => (
  <Box
    sx={{
      flex: 1,
      textAlign: "center",
      border: `1px solid ${total ? SZ.teal100 : SZ.line}`,
      background: total ? SZ.teal50 : SZ.card,
      borderRadius: SZ.radiusSm,
      p: "8px 6px",
    }}
  >
    <Txt
      component="div"
      sx={{
        fontFamily: SZ.mono,
        fontSize: 15,
        fontWeight: 600,
        color: total ? SZ.teal700 : SZ.ink900,
      }}
    >
      {value ?? "—"}
    </Txt>
    <Txt component="div" sx={{ fontSize: 9.5, color: SZ.ink400, mt: "1px" }}>
      {label}
    </Txt>
  </Box>
);

/**
 * Resolved inputs and where each came from. A `ResolvedInput` is
 * `{value, source, reliability, validated}` — `validated` only at the VERIFIED
 * precedence, so an entered or sizer-estimated value prices but is flagged. The
 * reliability dots are the response's own `reliability` (0–1), not a rating this
 * screen assigns.
 */
export const ProvenanceList = ({ items = [] }) => (
  <Box>
    {items.map((it) => (
      <Box
        key={it.label}
        sx={{
          py: 1.25,
          borderBottom: `1px solid ${SZ.line}`,
          "&:last-of-type": { borderBottom: "none" },
        }}
      >
        <Flx jb ac g={1.5}>
          <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: SZ.ink900 }}>
            {it.label}
          </Txt>
          <Txt
            sx={{
              fontFamily: it.mono === false ? "inherit" : SZ.mono,
              fontSize: 12.5,
              fontWeight: 600,
              color: SZ.ink900,
              textAlign: "right",
            }}
          >
            {it.value}
          </Txt>
        </Flx>
        <Flx ac g={0.75} sx={{ mt: 0.5, flexWrap: "wrap" }}>
          {(it.tags || []).map((t) => (
            <ProvTag key={t.label} {...t} />
          ))}
          {it.reliability != null && (
            <ReliabilityDots value={it.reliability} />
          )}
        </Flx>
      </Box>
    ))}
  </Box>
);

const ProvTag = ({ label, tone, tip }) => {
  const palette =
    tone === "warn"
      ? { bg: SZ.warnBg, fg: SZ.warn }
      : tone === "muted"
        ? { bg: "#eef2f3", fg: SZ.ink400 }
        : { bg: "#eef2f3", fg: SZ.ink700 };
  const chip = (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: "5px",
        background: palette.bg,
        color: palette.fg,
        cursor: tip ? "help" : "default",
      }}
    >
      {label}
    </Box>
  );
  return tip ? (
    <Tooltip title={tip} placement="top">
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

// Four dots, filled in quarters — a glanceable read of `reliability` (0–1).
const ReliabilityDots = ({ value }) => {
  const pct = Math.round(Number(value) * 100);
  const filled = Math.round((Number(value) || 0) * 4);
  return (
    <Flx ac g={0.375}>
      {[0, 1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: i < filled ? SZ.teal600 : SZ.lineStrong,
          }}
        />
      ))}
      <Txt sx={{ fontSize: 10, color: SZ.ink400, ml: 0.25 }}>{pct}%</Txt>
    </Flx>
  );
};

/* ============================================================ DSCR ladder */

/**
 * The priced coupon ladder. Row shape (quick_price_dscr):
 * `{coupon, basePrice, llpaTotal, price, dscr, applied[], clamped, passing,
 * ineligible}`. There is NO per-row payment on this response — the payment lives
 * on `result.pitia` and in the sensitivity cells, so the ladder shows price /
 * DSCR / LLPA instead of the P&I column the prototype had (it rendered "—" for
 * every row).
 *
 * `applied[]` names every LLPA row the sheet used — the auditable trail behind
 * the price — so it hangs off each row as a tooltip.
 */
export const LadderTable = ({ rows = [], bestCoupon }) => {
  const isBest = (r) => bestCoupon != null && r.coupon === bestCoupon;
  const muted = (r) => ({ color: r.ineligible ? SZ.ink400 : SZ.ink700 });

  return (
    <ResultTable
      rows={rows}
      minWidth={300}
      rowSx={(r) => ({
        ...(isBest(r) ? { background: SZ.teal50 } : null),
        ...(r.ineligible ? { opacity: 0.6 } : null),
      })}
      columns={[
        {
          label: "Coupon",
          align: "ctr",
          render: (r) => (
            <Tooltip
              title={
                (r.applied || []).length ? (
                  <Box sx={{ whiteSpace: "pre-line", fontSize: 11.5 }}>
                    {(r.applied || [])
                      .map((a) =>
                        Object.entries(a)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(""),
                      )
                      .join("\n")}
                  </Box>
                ) : (
                  ""
                )
              }
              placement="left"
              arrow
            >
              <Box
                component="span"
                sx={{
                  fontWeight: isBest(r) ? 700 : 500,
                  color: isBest(r) ? SZ.teal700 : SZ.ink900,
                  cursor: (r.applied || []).length ? "help" : "default",
                }}
              >
                {fmtPct(r.coupon, 3)}
              </Box>
            </Tooltip>
          ),
        },
        {
          label: "Price",
          align: "num",
          render: (r) => <Box component="span" sx={muted(r)}>{fmtPrice(r.price)}</Box>,
        },
        {
          label: "DSCR",
          align: "num",
          render: (r) => <Box component="span" sx={muted(r)}>{fmtRatio(r.dscr)}</Box>,
        },
        {
          label: "LLPA",
          align: "num",
          render: (r) => (
            <Box component="span" sx={muted(r)}>
              {r.llpaTotal == null ? "—" : fmtPrice(r.llpaTotal, 3)}
            </Box>
          ),
        },
        {
          label: "Status",
          align: "ctr",
          render: (r) =>
            isBest(r) ? (
              <Badge tone="pass">Best</Badge>
            ) : r.ineligible ? (
              <Badge tone="fail">N/E</Badge>
            ) : r.passing ? (
              <Badge tone="pass">Pass</Badge>
            ) : (
              <Box component="span" sx={{ color: SZ.ink400 }}>
                —
              </Box>
            ),
        },
      ]}
    />
  );
};

/* ============================================================ Sensitivity */

/**
 * The SERVER-GENERATED sensitivity matrix: coupon across (base ±50bps in
 * 12.5bps steps), loan amount down (base ±5% in 2.5% steps), each cell
 * `{dscr, pitia}`.
 *
 * Colors come from the response's own `bands` ({green, amber}) — shipped as
 * data precisely so the FE never hardcodes a threshold. Cells are never
 * recomputed here: changing an input means re-POSTing the scenario.
 *
 * A real `<table>` rather than AG Grid (CLAUDE.md's tables rule): this is a
 * fixed 5×9 HEAT MAP of one scenario, not a grid of records — the axes are
 * generated by the pricing engine, every cell is two stacked values with a band
 * color, and there is no sorting, filtering, paging or row identity for a grid
 * to manage. `<table>` is also what makes the row/column headers announce
 * correctly to a screen reader.
 */
export const SensitivityMatrix = ({ sensitivity }) => {
  // Crosshair state: the hovered cell's row/column, so the whole row, the whole
  // column and both axis headers light up together — a 5×9 grid of two-value
  // swatches is hard to read across otherwise. Declared ABOVE the empty guard;
  // a hook may not sit behind an early return.
  const [hover, setHover] = useState(null);
  if (!sensitivity?.cells?.length) return null;
  const { columns = [], rows = [], cells = [], base = {}, bands = {}, note } = sensitivity;
  const green = bands.green;
  const amber = bands.amber;
  const lit = (ri, ci) => hover != null && (hover.r === ri || hover.c === ci);

  const cellTone = (dscr) => {
    if (dscr == null || green == null || amber == null) return { fg: SZ.ink500, bg: "transparent" };
    if (dscr >= green) return { fg: SZ.good, bg: SZ.goodBg };
    if (dscr >= amber) return { fg: SZ.warn, bg: SZ.warnBg };
    return { fg: SZ.bad, bg: SZ.badBg };
  };

  return (
    <Box>
      {/* Same bordered block as ResultTable, but the matrix keeps its own
          hand-built markup: every cell is a two-value tinted swatch, and the
          column headers ARE data (the generated coupon axis), so the shared
          component's one-alignment-per-column contract doesn't apply. Header and
          cell alignment still agree — the axis column is left, every figure
          column is centred, top to bottom. */}
      <Box
        sx={{
          border: `1px solid ${SZ.line}`,
          borderRadius: SZ.radiusSm,
          overflow: "hidden",
          overflowX: "auto",
        }}
      >
        <Box
          component="table"
          sx={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: ".07em",
                  textTransform: "uppercase",
                  color: SZ.ink400,
                  textAlign: "left",
                  p: "8px 12px",
                  whiteSpace: "nowrap",
                  borderBottom: `1px solid ${SZ.line}`,
                  borderRight: `1px solid ${SZ.line}`,
                }}
              >
                Loan / Coupon
              </Box>
              {columns.map((c, ci) => (
                <Box
                  component="th"
                  key={ci}
                  sx={{
                    textAlign: "center",
                    p: "7px 6px",
                    whiteSpace: "nowrap",
                    borderBottom: `1px solid ${SZ.line}`,
                    background: hover?.c === ci ? SZ.teal50 : "transparent",
                    transition: "background .12s",
                  }}
                >
                  <Txt
                    component="div"
                    sx={{
                      fontFamily: SZ.mono,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 11.5,
                      fontWeight: c.isBase ? 700 : 500,
                      color: c.isBase || hover?.c === ci ? SZ.teal700 : SZ.ink900,
                    }}
                  >
                    {fmtPct(c.coupon, 3)}
                  </Txt>
                  {/* the axis IS data — `deltaBps` ships on every column, so the
                      step is labelled rather than left to be counted */}
                  <Txt
                    component="div"
                    sx={{ fontSize: 10, color: SZ.ink400, fontWeight: 500, mt: "1px" }}
                  >
                    {c.isBase
                      ? "quoted"
                      : `${c.deltaBps > 0 ? "+" : ""}${c.deltaBps}${
                          Math.abs(c.deltaBps) === 50 ? " bps" : ""
                        }`}
                  </Txt>
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((r, ri) => (
              <Box
                component="tr"
                key={ri}
                sx={{
                  "& > *": { borderBottom: `1px solid ${SZ.line}` },
                  "&:last-of-type > *": { borderBottom: "none" },
                }}
              >
                <Box
                  component="th"
                  sx={{
                    textAlign: "left",
                    p: "6px 12px",
                    whiteSpace: "nowrap",
                    borderRight: `1px solid ${SZ.line}`,
                    background: hover?.r === ri ? SZ.teal50 : "transparent",
                    transition: "background .12s",
                  }}
                >
                  <Txt
                    component="div"
                    sx={{
                      fontFamily: SZ.mono,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 11.5,
                      fontWeight: r.isBase ? 700 : 500,
                      color: r.isBase ? SZ.teal700 : SZ.ink900,
                    }}
                  >
                    {fmtMoney(r.loanAmount)}
                  </Txt>
                  <Txt component="div" sx={{ fontSize: 10.5, color: SZ.ink400 }}>
                    {r.deltaPct === 0
                      ? "base"
                      : `${r.deltaPct > 0 ? "+" : ""}${(r.deltaPct * 100).toFixed(1)}%`}
                    {r.ltv != null ? ` · LTV ${fmtPct(r.ltv, 1)}` : ""}
                  </Txt>
                </Box>
                {(cells[ri] || []).map((cell, ci) => {
                  const tone = cellTone(cell?.dscr);
                  const isBase = base.row === ri && base.col === ci;
                  return (
                    <Box
                      component="td"
                      key={ci}
                      onMouseEnter={() => setHover({ r: ri, c: ci })}
                      onMouseLeave={() => setHover(null)}
                      title={
                        cell?.dscr == null
                          ? undefined
                          : `DSCR ${Number(cell.dscr).toFixed(4)} · PITIA ${fmtMoney(cell.pitia)}/mo`
                      }
                      sx={{
                        p: "6px",
                        textAlign: "center",
                        background: tone.bg,
                        // The crosshair darkens the band tint rather than
                        // replacing it — the color still has to read as its band.
                        filter: lit(ri, ci) ? "brightness(.94)" : "none",
                        transition: "filter .12s",
                        outline: isBase ? `2px solid ${SZ.teal600}` : "none",
                        outlineOffset: "-2px",
                      }}
                    >
                      <Txt
                        component="div"
                        sx={{
                          fontFamily: SZ.mono,
                          fontVariantNumeric: "tabular-nums",
                          fontSize: 12,
                          fontWeight: isBase ? 700 : 600,
                          color: tone.fg,
                        }}
                      >
                        {cell?.dscr == null ? "—" : Number(cell.dscr).toFixed(2)}
                      </Txt>
                      <Txt component="div" sx={{ fontSize: 10, color: SZ.ink400 }}>
                        {fmtMoney(cell?.pitia)}
                      </Txt>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Flx g={2} sx={{ mt: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        {green != null && <LegendSwatch color={SZ.good} bg={SZ.goodBg} label={`DSCR ≥ ${green.toFixed(2)}`} />}
        {amber != null && <LegendSwatch color={SZ.warn} bg={SZ.warnBg} label={`≥ ${amber.toFixed(2)}`} />}
        {amber != null && (
          <LegendSwatch
            color={SZ.bad}
            bg={SZ.badBg}
            label={`< ${amber.toFixed(2)} — would not price`}
          />
        )}
        <Flx ac g={0.6}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "4px",
              border: `2px solid ${SZ.teal600}`,
            }}
          />
          <Txt component="span" sx={{ fontSize: 11.5, color: SZ.ink500 }}>
            Quoted scenario
          </Txt>
        </Flx>
        {note && (
          <Txt sx={{ fontSize: 11, color: SZ.ink400, fontStyle: "italic" }}>{note}</Txt>
        )}
      </Flx>
    </Box>
  );
};

const LegendSwatch = ({ color, bg, label }) => (
  <Flx ac g={0.6}>
    <Box sx={{ width: 12, height: 12, borderRadius: "4px", background: bg, border: `1px solid ${color}55` }} />
    <Txt component="span" sx={{ fontSize: 11.5, color: SZ.ink500 }}>
      {label}
    </Txt>
  </Flx>
);

/* ============================================================ Closing costs */

/**
 * The 2–4% closing-cost ladder — rows `{closingCostPct, totalDollars,
 * aprDragYr1, note}` computed backend-side and rendered as-is.
 */
export const ClosingCostLadder = ({ rows = [] }) => {
  if (!rows.length) return null;
  return (
    <Box>
      <ResultTable
        rows={rows}
        columns={[
          {
            label: "Closing cost",
            align: "num",
            render: (r) => fmtPct(r.closingCostPct, 2),
          },
          {
            label: "Total",
            align: "num",
            render: (r) => fmtMoney(r.totalDollars),
          },
          {
            label: "Yr-1 APR drag",
            align: "num",
            render: (r) => (
              <Box component="span" sx={{ color: SZ.ink500 }}>
                {fmtPct(r.aprDragYr1, 2)}
              </Box>
            ),
          },
        ]}
      />
      <Txt sx={{ fontSize: 11, color: SZ.ink400, mt: 0.75, fontStyle: "italic" }}>
        APR drag is a year-one rate equivalent; the quoted coupon is held constant.
      </Txt>
    </Box>
  );
};

/* ============================================================ Gates + notes */

/**
 * The DSCR quick-price 422 gate. Each entry is
 * `{llpa, missing[], note}` — inputs whose absence would silently skip an LLPA
 * table and MISPRICE the loan, so this is blocking, not advisory.
 */
export const MissingRequirementsCallout = ({ items = [] }) => {
  if (!items.length) return null;
  return (
    <Callout tone="bad" title="Cannot price — required inputs missing">
      {items.map((m, i) => {
        /* TWO ENTRY SHAPES, both legitimate. The LLPA gates arrive as
           `{llpa, missing: [names], note}` — one entry naming several fields,
           attributed to the sheet table it would have skipped. The newer input
           gates (`address.zip`) arrive as `{field, reason}` — one entry, one
           field, no table to attribute. Reading only the first shape is what
           rendered the ZIP gate as a blank heading over "undefined". */
        const fields = m.missing?.length ? m.missing : m.field ? [m.field] : [];
        const note = m.note || m.reason;
        return (
          <Box key={i} sx={{ mt: i ? 1 : 0.25 }}>
            <Txt component="div" sx={{ fontSize: 12.5, fontWeight: 600, color: SZ.bad }}>
              {fields.map(humanize).join(", ")}
            </Txt>
            <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink700 }}>
              {note}
              {m.llpa && (
                <Box component="span" sx={{ color: SZ.ink400 }}> ({m.llpa})</Box>
              )}
            </Txt>
          </Box>
        );
      })}
    </Callout>
  );
};

/**
 * `payloadWarnings[]` — the scenario still priced, but it used off-contract
 * field names the backend renamed for us. These are TEMPORARY aliases: Save Loan
 * and every other flow use the canonical names, so each one is a bug to fix, not
 * a notice to dismiss.
 */
export const PayloadWarnings = ({ warnings = [] }) => {
  if (!warnings.length) return null;
  return (
    <Callout tone="warn" title={`Payload normalized (${warnings.length})`} sx={{ mb: 1.5 }}>
      {warnings.map((w, i) => (
        <Txt key={i} component="div" sx={{ fontSize: 11.5, color: SZ.ink700, py: "1px" }}>
          • {w}
        </Txt>
      ))}
    </Callout>
  );
};

/**
 * `dataChecks[]` — the reconciliation surface of the PHASED INPUT CONTRACT
 * (2026-08-17). The sizer is the loose-input stage by design: a declared
 * guarantor count or a self-attested background answer is ACCEPTED and turned
 * into a check against real data instead of being rejected. Each entry is
 * something the scenario asserted that processing will verify against the credit
 * report, the background-report extraction or the entered borrower rows.
 *
 * NOT the same thing as `PayloadWarnings`, and they must not be shown as one:
 * a payload warning is a FE bug (an off-contract name that will not translate on
 * Save Loan); a data check is a legitimate declaration with a follow-up.
 *
 * Shapes the backend emits today:
 *   {check: 'declared_guarantor_count', declared, derivedFromBorrowers, note}
 *   {check: 'declared_background_flags', borrower, declared: [...], note}
 * Anything else renders from `note` alone, so a new check type needs no FE
 * change to appear.
 */
export const DataChecks = ({ checks = [] }) => {
  if (!checks.length) return null;
  const detail = (c) => {
    if (c.check === "declared_guarantor_count") {
      return `declared ${c.declared} · entered ${c.derivedFromBorrowers}`;
    }
    if (Array.isArray(c.declared) && c.declared.length) {
      return [c.borrower, c.declared.map(humanize).join(", ")]
        .filter(Boolean)
        .join(" · ");
    }
    return c.borrower || null;
  };

  return (
    <Callout
      tone="info"
      title={`To verify in processing (${checks.length})`}
      sx={{ mb: 1.5 }}
    >
      {checks.map((c, i) => (
        <Box key={`${c.check}-${i}`} sx={{ py: "3px" }}>
          <Flx ac g={0.75} sx={{ flexWrap: "wrap" }}>
            <Txt sx={{ fontSize: 11.5, fontWeight: 700, color: SZ.ink900 }}>
              {humanize(c.check || "check")}
            </Txt>
            {detail(c) && (
              <Txt
                sx={{
                  fontFamily: SZ.mono,
                  fontSize: 11,
                  color: SZ.ink700,
                }}
              >
                {detail(c)}
              </Txt>
            )}
          </Flx>
          {c.note && (
            <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink700, lineHeight: 1.5 }}>
              {c.note}
            </Txt>
          )}
        </Box>
      ))}
    </Callout>
  );
};

/**
 * A missing-field list, which is NOT uniformly a list of strings.
 * `PricingStructureBase.missing_fields` returns flat names for the `loan` scope
 * but PER-ROW objects for the repeaters:
 *
 *   loan:              ['interestRate']
 *   subjectProperties: [{index: 0, missing: ['unitCount']}]
 *
 * Mapping the whole thing through `humanize` turned every row object into
 * "[Object Object]". Rows are addressed by their 1-based position, because that
 * is how the form labels them ("Property 1").
 */
const formatMissingList = (fields) =>
  (Array.isArray(fields) ? fields : [])
    .map((f) => {
      if (typeof f === "string") return humanize(f);
      if (f && typeof f === "object" && Array.isArray(f.missing)) {
        const names = f.missing.map(humanize).join(", ");
        return f.index != null ? `#${f.index + 1} ${names}` : names;
      }
      return null;
    })
    .filter(Boolean)
    .join("; ");

// Thousands separators on a resolved figure — liquidity reads as 300,000 while
// a FICO of 760 is left alone.
const formatPendingValue = (v) =>
  typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("en-US") : String(v);

/**
 * `missingFields` + `subjectToChangePending` — the "terms subject to change"
 * affordance (shipping requirement 4). The engine still prices indicatively;
 * this says which inputs could move the terms once resolved.
 */
export const SubjectToChangeNote = ({ missingFields, pending = [] }) => {
  const lines = [];
  const unknown = [];

  // `pending[]` entries are OBJECTS — `price_and_screen` builds
  // `{input, reason, currentValue}` for an unvalidated input and
  // `{input, reason, missing[]}` for a missing-field scope. Neither carries a
  // `message`, which is why the original `p.message || JSON.stringify(p)` printed
  // raw objects. The known shapes read as sentences below; anything else drops to
  // JsonPreview rather than being stringified.
  pending.forEach((p) => {
    if (typeof p === "string") return lines.push(p);
    if (p && typeof p === "object" && (p.input || p.reason)) {
      const detail = Array.isArray(p.missing)
        ? formatMissingList(p.missing)
        : p.currentValue != null && p.currentValue !== ""
          ? `currently ${formatPendingValue(p.currentValue)}`
          : null;
      return lines.push(
        [humanize(p.input), p.reason, detail].filter(Boolean).join(" — "),
      );
    }
    unknown.push(p);
  });

  /* `subjectToChangePending` is a SUPERSET of `missingFields` — the engine walks
     the same three scopes and appends one pending entry per non-empty one. So
     missingFields is only read when pending is absent; rendering both listed
     every missing field twice ("Loan — missing required field(s) — Interest
     Rate" AND "Loan: Interest Rate"). */
  if (!lines.length && !unknown.length) {
    Object.entries(missingFields || {}).forEach(([scope, fields]) => {
      if (!Array.isArray(fields) || !fields.length) return;
      lines.push(`${humanize(scope)}: ${formatMissingList(fields)}`);
    });
  }

  const items = lines;
  if (!items.length && !unknown.length) return null;

  return (
    <Callout tone="warn" title="Indicative — terms subject to change">
      {items.slice(0, 8).map((it, i) => (
        <Txt key={i} component="div" sx={{ fontSize: 11.5, color: SZ.ink700, py: "1px" }}>
          • {it}
        </Txt>
      ))}
      {items.length > 8 && (
        <Txt sx={{ fontSize: 11, color: SZ.ink400, mt: 0.5 }}>+{items.length - 8} more</Txt>
      )}
      {unknown.length > 0 && (
        <Box sx={{ mt: items.length ? 1 : 0 }}>
          <JsonPreview
            values={unknown}
            sticky={false}
            showCollapseControls={false}
            defaultCollapse={2}
            fontSize={11}
          />
        </Box>
      )}
    </Callout>
  );
};

/** `disclaimer` — rendered VERBATIM with every priced result, per the contract. */
export const ScenarioDisclaimer = ({ text }) => {
  if (!text) return null;
  return (
    <Flx g={0.85} sx={{ alignItems: "flex-start" }}>
      <ReportProblemRoundedIcon sx={{ fontSize: 14, color: SZ.ink400, mt: "2px" }} />
      <Txt component="span" sx={{ fontSize: 11.5, color: SZ.ink500, lineHeight: 1.5 }}>
        {text}
      </Txt>
    </Flx>
  );
};

/**
 * `classExplanation` — the plain-language basis for the borrower class (FICO
 * source + points, cumulative guarantor experience + points, total vs the
 * VERSIONED class floors). Prelim pricing hangs entirely on the class, so this
 * sits directly under the classification card.
 */
export const ClassExplanation = ({ text }) => {
  if (!text) return null;
  return (
    <Box
      sx={{
        mt: 1,
        p: "9px 11px",
        borderRadius: SZ.radiusSm,
        background: "#fbfcfc",
        border: `1px solid ${SZ.line}`,
      }}
    >
      <Txt sx={{ fontSize: 11.5, color: SZ.ink700, lineHeight: 1.55 }}>{text}</Txt>
    </Box>
  );
};

/** Full-width card for content the 360px results rail can't hold (the matrix). */
export const WideCard = ({ title, subtitle, action, children, sx }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      overflow: "hidden",
      mt: 3,
      ...sx,
    }}
  >
    <Flx jb ac g={2} sx={{ p: "16px 20px", borderBottom: `1px solid ${SZ.line}`, flexWrap: "wrap" }}>
      <Box>
        <Txt sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.02em", color: SZ.ink900 }}>
          {title}
        </Txt>
        {subtitle && (
          <Txt sx={{ fontSize: 11.5, color: SZ.ink400, mt: "2px" }}>{subtitle}</Txt>
        )}
      </Box>
      {action}
    </Flx>
    <Box sx={{ p: "20px" }}>{children}</Box>
  </Box>
);
